import { useEffect, useState } from "react";
import { Edit3, Plus, Search, Trash2 } from "lucide-react";
import http from "../api/http";
import Modal from "../components/Modal";
import Pagination from "../components/Pagination";
import { useServerPagination } from "../utils/usePagination";
import { consumirIntencionNuevo } from "../utils/navigationIntent";

const estadoInicial = {
  nombre: "",
  apellido: "",
  usuario: "",
  password: "",
  confirmarPassword: "",
  dni: "",
  email: "",
  telefono: "",
  direccion: "",
  rol: "cajero",
};

const rolesUsuario = [
  { value: "administrador", label: "Administrador" },
  { value: "cajero", label: "Cajero" },
];

const labelsRol = {
  administrador: "Administrador",
  vendedor: "Vendedor",
  cajero: "Cajero",
};

export default function Usuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [formulario, setFormulario] = useState(estadoInicial);
  const [usuarioEditando, setUsuarioEditando] = useState(null);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const [error, setError] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const paginacion = useServerPagination();

  const obtenerUsuarios = async () => {
    try {
      setCargando(true);
      setError("");
      const { data } = await http.get("/usuarios", {
        params: { q: busqueda || undefined, page: paginacion.page, limit: paginacion.pageSize },
      });
      setUsuarios(Array.isArray(data?.items) ? data.items : []);
      paginacion.applyResponse(data);
    } catch (err) {
      setError(err.response?.data?.error || "No se pudieron cargar los usuarios.");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    if (consumirIntencionNuevo()) setModalAbierto(true);
  }, []);

  useEffect(() => {
    const timeout = window.setTimeout(obtenerUsuarios, 250);
    return () => window.clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busqueda, paginacion.page, paginacion.pageSize]);

  const usuariosFiltrados = usuarios;

  const limpiarMensajes = () => {
    setMensaje("");
    setError("");
  };

  const abrirCrear = () => {
    limpiarMensajes();
    setUsuarioEditando(null);
    setFormulario(estadoInicial);
    setModalAbierto(true);
  };

  const abrirEditar = (usuario) => {
    limpiarMensajes();
    setUsuarioEditando(usuario);
    setFormulario({
      ...estadoInicial,
      ...usuario,
      password: "",
      confirmarPassword: "",
      rol: usuario.rol === "administrador" ? "administrador" : "cajero",
    });
    setModalAbierto(true);
  };

  const cerrarModal = () => {
    setModalAbierto(false);
    setUsuarioEditando(null);
    setFormulario(estadoInicial);
    setError("");
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    limpiarMensajes();
    setFormulario((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validarFormulario = () => {
    if (!formulario.nombre.trim()) {
      setError("El nombre es obligatorio.");
      return false;
    }

    if (!formulario.apellido.trim()) {
      setError("El apellido es obligatorio.");
      return false;
    }

    if (!formulario.usuario.trim()) {
      setError("El nombre de usuario es obligatorio.");
      return false;
    }

    if (!/^[a-zA-Z0-9._-]{3,40}$/.test(formulario.usuario.trim())) {
      setError(
        "El nombre de usuario debe tener entre 3 y 40 caracteres y solo puede usar letras, numeros, punto, guion o guion bajo.",
      );
      return false;
    }

    if (!usuarioEditando && !formulario.password) {
      setError("La contrasena es obligatoria.");
      return false;
    }

    if (formulario.password && formulario.password.length < 6) {
      setError("La contrasena debe tener al menos 6 caracteres.");
      return false;
    }

    if (formulario.password !== formulario.confirmarPassword) {
      setError("Las contrasenas no coinciden.");
      return false;
    }

    if (!formulario.dni.trim()) {
      setError("El DNI es obligatorio.");
      return false;
    }

    if (!formulario.email.trim() || !/.+@.+\..+/.test(formulario.email)) {
      setError("Ingresá un email válido.");
      return false;
    }

    if (!formulario.rol) {
      setError("Seleccioná un rol.");
      return false;
    }

    if (!/^[+\d\s().-]+$/.test(formulario.telefono.trim()) ||
        formulario.telefono.replace(/\D/g, "").length < 10) {
      setError("Ingrese un telefono valido con WhatsApp, incluyendo el codigo de area.");
      return false;
    }

    return true;
  };

  const guardarUsuario = async (event) => {
    event.preventDefault();

    if (!validarFormulario()) return;

    try {
      setCargando(true);
      setError("");
      const payload = { ...formulario };
      delete payload.confirmarPassword;

      if (!payload.password) {
        delete payload.password;
      }

      if (usuarioEditando) {
        await http.put(`/usuarios/${usuarioEditando._id}`, payload);
        cerrarModal();
        setMensaje("Usuario actualizado correctamente.");
      } else {
        await http.post("/usuarios", payload);
        cerrarModal();
        setMensaje("Usuario creado correctamente.");
      }

      await obtenerUsuarios();
    } catch (err) {
      setError(err.response?.data?.error || "No se pudo guardar el usuario.");
    } finally {
      setCargando(false);
    }
  };

  const eliminarUsuario = async (id) => {
    if (!window.confirm("¿Seguro que querés eliminar este usuario?")) return;

    try {
      setCargando(true);
      setError("");
      await http.delete(`/usuarios/${id}`);
      setMensaje("Usuario eliminado correctamente.");
      await obtenerUsuarios();
    } catch (err) {
      setError(err.response?.data?.error || "No se pudo eliminar el usuario.");
    } finally {
      setCargando(false);
    }
  };

  return (
    <div>
      <div className="page-header actions-only">
        <div className="header-actions">
          <button className="btn btn-primary" onClick={abrirCrear}>
            <Plus size={18} />
            Nuevo usuario
          </button>
        </div>
      </div>

      {mensaje && <div className="alert alert-ok">{mensaje}</div>}
      {error && !modalAbierto && <div className="alert alert-error">{error}</div>}

      <section className="panel">
        <div className="toolbar">
          <div>
            <h2>Usuarios cargados</h2>
            <p>Total: {paginacion.totalItems}</p>
          </div>
          <div className="search">
            <Search size={17} />
            <input
              placeholder="Buscar por nombre, usuario, DNI, email, telefono o rol..."
              value={busqueda}
              onChange={(event) => {
                setBusqueda(event.target.value);
                paginacion.resetPage();
              }}
            />
          </div>
        </div>

        {cargando ? (
          <div className="loading-state">Cargando usuarios...</div>
        ) : usuariosFiltrados.length === 0 ? (
          <div className="empty-state">No hay usuarios para mostrar.</div>
        ) : (
          <>
            <div className="table-wrap">
              <table>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Usuario</th>
                  <th>DNI</th>
                  <th>Email</th>
                  <th>Teléfono</th>
                  <th>Dirección</th>
                  <th>Rol</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {usuariosFiltrados.map((usuario) => (
                  <tr key={usuario._id}>
                    <td>
                      <strong>
                        {usuario.nombre || "-"} {usuario.apellido || ""}
                      </strong>
                      <span className="subtext">{usuario.email || "-"}</span>
                    </td>
                    <td>{usuario.usuario || "-"}</td>
                    <td>{usuario.dni || "-"}</td>
                    <td>{usuario.email || "-"}</td>
                    <td>{usuario.telefono || "-"}</td>
                    <td>{usuario.direccion || "-"}</td>
                    <td>
                      <span className={`badge role-${usuario.rol || "vendedor"}`}>
                        {labelsRol[usuario.rol] || usuario.rol || "-"}
                      </span>
                    </td>
                    <td>
                      <div className="row-actions">
                        <button
                          className="btn action-icon-button"
                          type="button"
                          title="Editar"
                          aria-label="Editar"
                          onClick={() => abrirEditar(usuario)}
                        >
                          <Edit3 size={16} />
                        </button>
                        <button
                          className="btn btn-danger action-icon-button"
                          type="button"
                          title="Eliminar"
                          aria-label="Eliminar"
                          onClick={() => eliminarUsuario(usuario._id)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
            <Pagination
              page={paginacion.page}
              pageSize={paginacion.pageSize}
              totalItems={paginacion.totalItems}
              totalPages={paginacion.totalPages}
              onPageChange={paginacion.setPage}
              onPageSizeChange={paginacion.setPageSize}
            />
          </>
        )}
      </section>

      <Modal
        abierto={modalAbierto}
        titulo={usuarioEditando ? "Editar usuario" : "Cargar nuevo usuario"}
        subtitulo={
          usuarioEditando
            ? `Modificando: ${usuarioEditando?.nombre || ""} ${usuarioEditando?.apellido || ""}`
            : "Completá los datos para registrar un usuario interno."
        }
        onClose={cerrarModal}
        footer={
          <>
            <button className="btn" type="button" onClick={cerrarModal}>
              Cancelar
            </button>
            <button className="btn btn-primary" form="usuario-form" type="submit" disabled={cargando}>
              {usuarioEditando ? "Actualizar usuario" : "Guardar usuario"}
            </button>
          </>
        }
      >
        {error && <div className="alert alert-error">{error}</div>}
        <form id="usuario-form" onSubmit={guardarUsuario}>
          <section className="form-section">
            <h3>Acceso al sistema</h3>
            <div className="form-grid two">
              <Field
                label="Nombre de usuario *"
                name="usuario"
                value={formulario.usuario}
                onChange={handleChange}
                autoComplete="username"
              />
              <Field
                label={usuarioEditando ? "Nueva contrasena" : "Contrasena *"}
                type="password"
                name="password"
                value={formulario.password}
                onChange={handleChange}
                autoComplete="new-password"
              />
              <Field
                label={usuarioEditando ? "Confirmar nueva contrasena" : "Confirmar contrasena *"}
                type="password"
                name="confirmarPassword"
                value={formulario.confirmarPassword}
                onChange={handleChange}
                autoComplete="new-password"
              />
            </div>
          </section>

          <section className="form-section">
            <h3>Datos del usuario</h3>
            <div className="form-grid two">
              <Field label="Nombre *" name="nombre" value={formulario.nombre} onChange={handleChange} />
              <Field label="Apellido *" name="apellido" value={formulario.apellido} onChange={handleChange} />
              <Field label="DNI *" name="dni" value={formulario.dni} onChange={handleChange} />
              <Select label="Rol *" name="rol" value={formulario.rol} onChange={handleChange} options={rolesUsuario} />
            </div>
          </section>

          <section className="form-section">
            <h3>Contacto</h3>
            <div className="form-grid two">
              <Field label="Email *" type="text" inputMode="email" name="email" value={formulario.email} onChange={handleChange} />
              <Field label="Telefono con WhatsApp *" type="tel" name="telefono" value={formulario.telefono} onChange={handleChange} placeholder="3412523117 o +5493412523117" required />
              <TextArea label="Dirección" name="direccion" value={formulario.direccion} onChange={handleChange} />
            </div>
          </section>
        </form>
      </Modal>
    </div>
  );
}

function Field({ label, ...props }) {
  return (
    <div className="field">
      <label>{label}</label>
      <input type="text" {...props} />
    </div>
  );
}

function Select({ label, options, ...props }) {
  return (
    <div className="field">
      <label>{label}</label>
      <select {...props}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function TextArea({ label, ...props }) {
  return (
    <div className="field">
      <label>{label}</label>
      <textarea rows="3" {...props} />
    </div>
  );
}
